import {
  ALL_CHART_TYPES,
  BLACK_HEX,
  DEFAULT_LINE_THICKNESS,
  DEFAULT_NORMALIZE_BY,
  DEFAULT_NORMALIZE_BY_SETTING
} from "constants/chart.constants";
import { EvaSource, TimeStep } from "constants/charts.enums";
import { FIELD_NAMES } from "constants/fieldLabels.constants";
import { createMergeWithDefaults } from "entities/utils";
import {
  TotalRateDateChartSettings,
  WellContributionChartRequestType,
  WellContributionChartSettings
} from "types";

import { MaxBinsSortOrderEnum } from "models/binSize";
import { EntityKind } from "models/entityKind";

import { BASE_CHART_REQUEST } from "./defaults";

export const DEFAULT_WELL_CONTRIBUTION_CHART_SETTINGS: Partial<WellContributionChartSettings> =
  {
    product: "Oil",
    otherProducts: [],
    chartType: ALL_CHART_TYPES.WellContribution.id,
    showForecast: false,
    showWellCount: true,
    averageSettings: {
      color: BLACK_HEX,
      thickness: DEFAULT_LINE_THICKNESS
    },
    showAverage: false,
    p10Settings: {
      color: BLACK_HEX,
      thickness: DEFAULT_LINE_THICKNESS
    },
    showP10: false,
    p50Settings: {
      color: BLACK_HEX,
      thickness: DEFAULT_LINE_THICKNESS
    },
    showP50: false,
    p90Settings: {
      color: BLACK_HEX,
      thickness: DEFAULT_LINE_THICKNESS
    },
    showP90: false,
    pdenDataSourceSetting: {
      source: EvaSource.Public,
      timeStep: TimeStep.Month
    },
    forecastMode: "forecastTrump",
    useForecastStartDate: false,
    survivorBias: true,
    rateType: 0,
    showIndividualSeries: false,
    cutoff: 50,
    timeStep: TimeStep.Month,
    shutInMonths: 3,
    normalizeBy: DEFAULT_NORMALIZE_BY,
    useWeightedRatioAverage: true,
    xVariable: {
      valueProperty: "Dates.Vintage_Year",
      displayTitle: "Vintage",
      canBin: true,
      dataType: "Integer",
      mappingName: "Dates.Vintage_Year",
      bin: null
    },
    yVariable: {
      valueProperty: "Well_Design.HzLength_m",
      displayTitle: "HzLengthEstimate",
      canBin: true,
      dataType: "Number",
      mappingName: "Well_Design.HzLength_m"
    },
    lockUnits: false,
    movingAverageDays: null,
    showLicensedVolumeLine: false,
    useChartFocus: false
  };

export const DEFAULT_WELL_CONTRIBUTION_REQUEST: Partial<WellContributionChartRequestType> =
  {
    ...BASE_CHART_REQUEST,
    FilterId: "",
    WellFilterId: "",
    requestId: "",
    ReverseColor: false,
    ColorPalette: {
      id: "-6",
      name: "Portland",
      shared: false,
      colors: ["rgb(12,51,131)"],
      preferredColors: [],
      paletteType: 0,
      colorScale: [],
      specialColors: null,
      thickness: [2],
      reverse: false
    },
    GroupBy: {
      title: "Operator",
      property: "Header.OperatorName",
      groupByField: "Header.OperatorName",
      pdenSource: EvaSource.Public,
      canBin: false,
      dataType: "Text",
      categoryId: 1,
      tooltip: "",
      display: "",
      entityKind: EntityKind.Facility,
      bin: {
        BinSize: 0,
        MinSize: null,
        MaxBins: null,
        MaxBinsSortOrder: MaxBinsSortOrderEnum.WellCount,
        GreaterThan: null,
        LessThan: null,
        Quantile: {
          quantileType: "NumberOfQuantile",
          numQuantiles: 4
        },
        BinType: "BinSize",
        UseDynamicBins: false
      },
      mode: undefined,
      forecastTrumpingSettings: undefined,
      performanceDateRange: undefined
    },
    sortBy: "AlphabeticalAsc",
    userForecast: [],
    userForecastSettings: {
      forecastSourceFolder: null,
      excludeMcDanielForecasts: false,
      reserveCategory: null,
      excludeNoviForecasts: true,
      specialForecastFolders: ["McDaniel Research"]
    },
    forecastMode: "forecastTrump",
    useForecastStartDate: false,
    showGroupsNotInFilter: true,
    placeholderBinsEnabled: false,
    hasCheckedBins: true,
    syncClientPden: false,
    syncClientSchema: "",
    colorLockedItems: {},
    syncClientFcst: false,
    syncForecastRescat: "2111",
    chartFieldInformation: {
      groupByField: {
        defaultUnit: "",
        product: "",
        property: "Header.Objective",
        title: "Objective",
        type: "groupBy",
        performanceDateRange: undefined
      },
      xField: {
        property: "Dates.Vintage_Year",
        title: "Vintage",
        type: "x",
        performanceDateRange: undefined
      },
      yField: {
        property: "Well_Design.HzLength_m",
        title: FIELD_NAMES.HZ_LENGTH,
        type: "y",
        performanceDateRange: undefined
      }
    },
    normalizeBySetting: DEFAULT_NORMALIZE_BY_SETTING,
    chartSetting: DEFAULT_WELL_CONTRIBUTION_CHART_SETTINGS as TotalRateDateChartSettings,
    axisMinMax: {
      xMin: null,
      xMax: null,
      yMin: null,
      yMax: null
    },
    isDefaultLockOn: true
  };

export const generateWellContributionRequestBody = createMergeWithDefaults(
  DEFAULT_WELL_CONTRIBUTION_REQUEST
);
